import type { Row } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import {
  NonEntityColumn,
  type NonEntityColumnProps,
} from "../../NonEntityColumn";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import { useIsMounted } from "@shared/frontend/hooks";
import { useHyperTable } from "../../HyperTable";
import type { ActionButtonAfter } from "../hyper-table-actions-bar/ActionsBar";

export interface SingleRowAction<Entity> {
  text: string;
  icon: React.ReactNode;
  infoTooltip?: string;
  className?: string;
  onClick: (
    e: React.MouseEvent<HTMLElement>,
    row: Row<Entity>,
  ) =>
    | Pick<ActionButtonAfter, "refetch">
    | undefined
    | Promise<Pick<ActionButtonAfter, "refetch"> | undefined>
    | Promise<void>
    | void;
}

export type SingleRowActionGetter<Entity> = (
  selectedRow: Row<Entity>,
) => SingleRowAction<Entity>[];

interface HyperTableMenuActionsColumnProps<Entity>
  extends Omit<Partial<NonEntityColumnProps<Entity>>, "valueConverter"> {
  actions: SingleRowAction<Entity>[] | SingleRowActionGetter<Entity>;
  menuClosedIcon?: React.ReactNode;
  menuOpenIcon?: React.ReactNode;
}

const HyperTableMenuActionsColumn = <Entity extends any>(
  props: HyperTableMenuActionsColumnProps<Entity>,
) => {
  const { actions, menuClosedIcon, menuOpenIcon, ...override } = props;
  const { refetch } = useHyperTable<Entity>();
  const isMounted = useIsMounted();
  const [menu, setMenu] = useState<{
    row: Row<Entity>;
    anchorElement: HTMLButtonElement;
  } | null>(null);
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const actionsArr = useMemo(() => {
    if (menu === null) return [];
    return actions instanceof Function ? actions(menu.row) : actions;
  }, [actions, menu]);

  return (
    <>
      <Menu
        open={menuOpen && !!menu && document.body.contains(menu.anchorElement)}
        anchorEl={menu?.anchorElement}
        onClose={() => setMenuOpen(false)}
      >
        {actionsArr.map(
          ({ icon, onClick, text, className, infoTooltip }, i) => (
            <MenuItem
              onClick={async (e) => {
                const row = menu!.row;
                setMenuOpen(false);
                const { refetch: shouldRefetch } =
                  (await onClick(e, row)) ?? {};

                if (isMounted.current && shouldRefetch) {
                  refetch(
                    typeof shouldRefetch === "boolean"
                      ? "remove"
                      : shouldRefetch,
                  );
                }
              }}
              className={className}
              key={`${i}_${text}`}
            >
              <Tooltip
                sx={{
                  width: "100%",
                }}
                title={infoTooltip}
                placement="left"
              >
                <Stack
                  width="100%"
                  height="100%"
                  display="flex"
                  flexDirection="row"
                  gap="3px"
                >
                  {icon}
                  {text}
                </Stack>
              </Tooltip>
            </MenuItem>
          ),
        )}
      </Menu>
      <NonEntityColumn<Entity>
        cell={({ row }) => {
          return (
            <Stack
              width="100%"
              height="100%"
              alignItems="center"
              justifyContent="center"
            >
              <IconButton
                sx={{ cursor: "pointer" }}
                key={row.id}
                onClick={(e) => {
                  setMenu({ anchorElement: e.currentTarget, row });
                  setMenuOpen(true);
                }}
                ref={(r) => {
                  if (r && menu && menu.row.id === row.id) {
                    setMenu({ row, anchorElement: r });
                  }
                }}
              >
                {menu?.row.id === row.id && menuOpen
                  ? (menuOpenIcon ?? <MenuOpenIcon />)
                  : (menuClosedIcon ?? <MenuIcon />)}
              </IconButton>
            </Stack>
          );
        }}
        deps={[
          setMenu,
          actions,
          menu?.row.id,
          menuOpen,
          menuOpenIcon,
          menuClosedIcon,
        ]}
        enableSorting={false}
        enablePinning={false}
        enableResizing={false}
        dontShowHeaderDivider={true}
        preventRowClick={true}
        defaultPinned
        size={50}
        minSize={50}
        maxSize={50}
        {...override}
      />
    </>
  );
};

export default HyperTableMenuActionsColumn;
